import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import SEOHead from '../components/SEOHead';
import { Badge } from '../components/ui/badge';
import { 
  ArrowRight, Sparkles, Zap, Shield, Globe,
  Code2, Type, Search, Repeat, QrCode, Key, Hash, FileJson,
  Database, Link2, Star, Calculator, TrendingUp, Calendar
} from 'lucide-react';
import { motion } from 'framer-motion';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Featured tools for homepage
const featuredTools = [
  {
    slug: 'qr-code-generator',
    name: 'QR Code Generator',
    description: 'Generate QR codes for URLs, text, WiFi, and contact info.',
    icon: QrCode,
    color: 'from-violet-500/20 to-purple-500/20',
    iconColor: 'text-violet-400'
  },
  {
    slug: 'json-formatter',
    name: 'JSON Formatter',
    description: 'Format, beautify, and validate JSON data instantly.',
    icon: FileJson,
    color: 'from-amber-500/20 to-orange-500/20',
    iconColor: 'text-amber-400'
  },
  {
    slug: 'password-generator',
    name: 'Password Generator',
    description: 'Create secure random passwords with custom options.',
    icon: Key,
    color: 'from-emerald-500/20 to-green-500/20',
    iconColor: 'text-emerald-400'
  },
  {
    slug: 'base64-encoder',
    name: 'Base64 Encoder',
    description: 'Encode and decode Base64 strings instantly.',
    icon: Code2,
    color: 'from-blue-500/20 to-cyan-500/20',
    iconColor: 'text-blue-400'
  },
  {
    slug: 'sql-formatter',
    name: 'SQL Formatter',
    description: 'Beautify SQL queries with proper formatting.',
    icon: Database,
    color: 'from-pink-500/20 to-rose-500/20',
    iconColor: 'text-pink-400'
  },
  {
    slug: 'slug-generator',
    name: 'Slug Generator',
    description: 'Create SEO-friendly URL slugs from any text.',
    icon: Link2,
    color: 'from-teal-500/20 to-cyan-500/20',
    iconColor: 'text-teal-400'
  },
  {
    slug: 'uuid-generator',
    name: 'UUID Generator',
    description: 'Generate unique identifiers for databases.',
    icon: Hash,
    color: 'from-indigo-500/20 to-blue-500/20',
    iconColor: 'text-indigo-400'
  },
  {
    slug: 'json-to-csv',
    name: 'JSON to CSV',
    description: 'Convert JSON arrays to CSV format for spreadsheets.',
    icon: Repeat,
    color: 'from-orange-500/20 to-red-500/20',
    iconColor: 'text-orange-400'
  },
  {
    slug: 'case-converter',
    name: 'Text Case Converter',
    description: 'Convert between UPPER, lower, Title, camelCase.',
    icon: Type,
    color: 'from-purple-500/20 to-pink-500/20',
    iconColor: 'text-purple-400'
  },
  {
    slug: 'url-encoder',
    name: 'URL Encoder',
    description: 'Encode and decode URLs and query parameters.',
    icon: Search,
    color: 'from-cyan-500/20 to-blue-500/20',
    iconColor: 'text-cyan-400'
  },
];

// Updated categories linking to SEO category pages
const categories = [
  { id: 'text-tools', name: 'Text Tools', icon: Type, count: 20, href: '/category/text-tools' },
  { id: 'developer-tools', name: 'Developer Tools', icon: Code2, count: 25, href: '/category/developer-tools' },
  { id: 'seo-tools', name: 'SEO Tools', icon: Search, count: 15, href: '/category/seo-tools' },
  { id: 'generators', name: 'Generators', icon: Sparkles, count: 20, href: '/category/generators' },
  { id: 'converters', name: 'Converters', icon: Repeat, count: 20, href: '/category/converters' },
];

const features = [
  {
    icon: Sparkles,
    title: '180+ Free Tools',
    description: 'Access our complete collection of tools without any signup or payment.',
  },
  {
    icon: Zap,
    title: 'Lightning Fast',
    description: 'All tools run locally in your browser for instant results.',
  },
  {
    icon: Shield,
    title: 'Privacy First',
    description: 'Your data never leaves your browser. We never store anything.',
  },
  {
    icon: Globe,
    title: 'Works Everywhere',
    description: 'Desktop, tablet, or mobile - our tools work on any device.',
  },
];

// Popular crypto calculators for SEO
const cryptoCalculators = [
  { slug: 'crypto-profit-calculator', name: 'Crypto Profit Calculator', icon: Calculator, color: 'text-orange-400', bg: 'bg-orange-500/20' },
  { slug: 'bitcoin-profit-calculator', name: 'Bitcoin Calculator', icon: TrendingUp, color: 'text-amber-400', bg: 'bg-amber-500/20' },
  { slug: 'ethereum-profit-calculator', name: 'Ethereum Calculator', icon: Calculator, color: 'text-blue-400', bg: 'bg-blue-500/20' },
  { slug: 'crypto-tax-calculator', name: 'Crypto Tax Calculator', icon: Calculator, color: 'text-red-400', bg: 'bg-red-500/20' },
  { slug: 'crypto-roi-calculator', name: 'ROI Calculator', icon: TrendingUp, color: 'text-green-400', bg: 'bg-green-500/20' },
];

const LandingPage = () => {
  const { login } = useAuth();
  const [latestPosts, setLatestPosts] = useState([]);

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        const response = await fetch(`${API_URL}/api/blog`);
        const data = await response.json();
        setLatestPosts(data.slice(0, 6)); // Get latest 6 posts
      } catch (error) {
        console.error('Error fetching posts:', error);
      }
    };
    fetchPosts();
  }, []);

  // Generate structured data for homepage
  const websiteSchema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "ToolsForge",
    "url": "https://toolsforgeapp.com",
    "description": "Free online tools for developers, designers, and content creators",
    "potentialAction": {
      "@type": "SearchAction",
      "target": "https://toolsforgeapp.com/all-tools?search={search_term_string}",
      "query-input": "required name=search_term_string"
    }
  };

  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "ToolsForge",
    "url": "https://toolsforgeapp.com",
    "description": "Collection of 180+ free online tools for developers, designers, and content creators"
  };

  return (
    <>
      <SEOHead
        title="ToolsForge - Free Online Tools for Developers & Creators"
        description="Discover 180+ free online tools. QR code generator, JSON formatter, password generator, Base64 encoder, and more. No signup required. All tools run in your browser."
        keywords="free online tools, developer tools, QR code generator, JSON formatter, password generator, Base64 encoder, URL encoder, text tools, SEO tools"
      />

      {/* Structured Data */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(websiteSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(organizationSchema) }} />

      <div className="min-h-screen" data-testid="landing-page">
        {/* Hero Section */}
        <section className="relative pt-32 pb-16 px-6 overflow-hidden">
          <div className="absolute inset-0 hero-glow" />
          <div className="absolute inset-0 grid-bg opacity-50" />
          
          <div className="max-w-7xl mx-auto relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center max-w-4xl mx-auto"
            >
              <Badge variant="outline" className="mb-6 px-4 py-1.5 border-primary/30 text-primary">
                <Sparkles className="w-3 h-3 mr-2" />
                180+ Free Tools
              </Badge>
              
              <h1 className="text-5xl md:text-7xl font-bold tracking-tight leading-none mb-6 font-['Plus_Jakarta_Sans']">
                All Your Digital Tools
                <span className="block gradient-text">In One Place</span>
              </h1>
              
              <p className="text-lg md:text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
                Free online tools for developers, designers, and creators. 
                No signup, no ads, no data collection. Just tools that work.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button 
                  size="lg" 
                  className="glow-blue text-base px-8"
                  asChild
                >
                  <Link to="/all-tools" data-testid="hero-browse-tools">
                    Browse All Tools
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Link>
                </Button>
                <Button 
                  size="lg" 
                  variant="outline" 
                  className="text-base px-8"
                  asChild
                >
                  <Link to="/compare/best-free-online-tools" data-testid="hero-top-tools">
                    <Star className="w-4 h-4 mr-2" />
                    See Top Picks
                  </Link>
                </Button>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Featured Tools Section */}
        <section className="py-16 px-6" data-testid="featured-tools-section">
          <div className="max-w-7xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="text-center mb-10"
            >
              <Badge variant="outline" className="mb-4 px-3 py-1 border-amber-500/30 text-amber-400">
                <Star className="w-3 h-3 mr-1" />
                Most Popular
              </Badge>
              <h2 className="text-3xl md:text-4xl font-semibold tracking-tight mb-3 font-['Plus_Jakarta_Sans']">
                Featured Tools
              </h2>
              <p className="text-muted-foreground max-w-xl mx-auto">
                Our most-used tools, loved by developers and creators worldwide
              </p>
            </motion.div>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
              {featuredTools.map((tool, index) => (
                <motion.div
                  key={tool.slug}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.05 }}
                >
                  <Link
                    to={`/tools/${tool.slug}`}
                    className="group flex flex-col h-full p-4 rounded-xl border border-border bg-card hover:border-primary/50 hover:bg-card/80 transition-all"
                    data-testid={`featured-tool-${tool.slug}`}
                  >
                    <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${tool.color} flex items-center justify-center mb-3`}>
                      <tool.icon className={`w-5 h-5 ${tool.iconColor}`} />
                    </div>
                    <h3 className="font-semibold text-sm mb-1 group-hover:text-primary transition-colors line-clamp-1">
                      {tool.name}
                    </h3>
                    <p className="text-xs text-muted-foreground line-clamp-2 flex-1">
                      {tool.description}
                    </p>
                    <div className="flex items-center text-primary text-xs mt-2 opacity-0 group-hover:opacity-100 transition-opacity">
                      Use Tool <ArrowRight className="w-3 h-3 ml-1" />
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>

            <div className="text-center mt-8">
              <Link to="/all-tools">
                <Button variant="outline" className="gap-2">
                  View All 180+ Tools
                  <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
            </div>
          </div>
        </section>

        {/* Categories Section */}
        <section className="py-16 px-6 bg-muted/20" data-testid="categories-section">
          <div className="max-w-7xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="text-center mb-10"
            >
              <h2 className="text-3xl md:text-4xl font-semibold tracking-tight mb-3 font-['Plus_Jakarta_Sans']">
                Browse by Category
              </h2>
              <p className="text-muted-foreground">
                Find the perfect tool for your needs
              </p>
            </motion.div>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
              {categories.map((category, index) => (
                <motion.div
                  key={category.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <Link
                    to={category.href}
                    className="flex flex-col items-center p-6 rounded-xl border border-border bg-card hover:border-primary/50 hover:bg-card/80 transition-all group"
                    data-testid={`category-${category.id}`}
                  >
                    <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
                      <category.icon className="w-6 h-6 text-primary" />
                    </div>
                    <span className="font-medium text-sm text-center">{category.name}</span>
                    <span className="text-xs text-muted-foreground mt-1">{category.count} tools</span>
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Popular Crypto Calculators - NEW SEO SECTION */}
        <section className="py-16 px-6" data-testid="crypto-calculators-section">
          <div className="max-w-7xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="text-center mb-10"
            >
              <Badge variant="outline" className="mb-4 px-3 py-1 border-orange-500/30 text-orange-400">
                <TrendingUp className="w-3 h-3 mr-1" />
                Updated 2026
              </Badge>
              <h2 className="text-3xl md:text-4xl font-semibold tracking-tight mb-3 font-['Plus_Jakarta_Sans']">
                Free Crypto Calculators
              </h2>
              <p className="text-muted-foreground max-w-xl mx-auto">
                Calculate Bitcoin, Ethereum, and altcoin profits, taxes, and ROI instantly
              </p>
            </motion.div>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-8">
              {cryptoCalculators.map((calc, index) => (
                <motion.div
                  key={calc.slug}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.05 }}
                >
                  <Link
                    to={`/tools/${calc.slug}`}
                    className="group flex flex-col items-center p-5 rounded-xl border border-border bg-card hover:border-orange-500/50 hover:bg-card/80 transition-all"
                    data-testid={`crypto-calc-${calc.slug}`}
                  >
                    <div className={`w-12 h-12 rounded-xl ${calc.bg} flex items-center justify-center mb-3 group-hover:scale-110 transition-transform`}>
                      <calc.icon className={`w-6 h-6 ${calc.color}`} />
                    </div>
                    <span className="font-medium text-sm text-center group-hover:text-orange-400 transition-colors">{calc.name}</span>
                    <span className="text-xs text-muted-foreground mt-1">Free Tool</span>
                  </Link>
                </motion.div>
              ))}
            </div>

            <div className="text-center">
              <Link to="/crypto-calculators">
                <Button variant="outline" className="gap-2 border-orange-500/30 text-orange-400 hover:bg-orange-500/10">
                  View All 15 Crypto Calculators
                  <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
            </div>
          </div>
        </section>

        {/* Latest Blog Posts - NEW SEO SECTION */}
        {latestPosts.length > 0 && (
          <section className="py-16 px-6 bg-muted/20" data-testid="latest-posts-section">
            <div className="max-w-7xl mx-auto">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
                className="text-center mb-10"
              >
                <Badge variant="outline" className="mb-4 px-3 py-1 border-green-500/30 text-green-400">
                  <Calendar className="w-3 h-3 mr-1" />
                  Fresh Content
                </Badge>
                <h2 className="text-3xl md:text-4xl font-semibold tracking-tight mb-3 font-['Plus_Jakarta_Sans']">
                  Latest Guides & Tutorials
                </h2>
                <p className="text-muted-foreground max-w-xl mx-auto">
                  Expert guides on crypto profits, taxes, and calculator usage
                </p>
              </motion.div>

              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
                {latestPosts.map((post, index) => (
                  <motion.article
                    key={post.slug}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: index * 0.05 }}
                    className="group p-5 rounded-xl border border-border bg-card hover:border-primary/50 transition-all"
                  >
                    <Link to={`/blog/${post.slug}`}>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground mb-2">
                        <Calendar className="w-3 h-3" />
                        {new Date(post.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                        <Badge variant="outline" className="text-xs px-1.5 py-0 border-green-500/30 text-green-400">2026</Badge>
                      </div>
                      <h3 className="font-semibold text-sm mb-2 group-hover:text-primary transition-colors line-clamp-2">
                        {post.title}
                      </h3>
                      <p className="text-xs text-muted-foreground line-clamp-2">
                        {post.excerpt}
                      </p>
                    </Link>
                  </motion.article>
                ))}
              </div>

              <div className="text-center">
                <Link to="/blog">
                  <Button variant="outline" className="gap-2">
                    Read All Articles
                    <ArrowRight className="w-4 h-4" />
                  </Button>
                </Link>
              </div>
            </div>
          </section>
        )}

        {/* Features Section */}
        <section className="py-16 px-6" data-testid="features-section">
          <div className="max-w-7xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="text-center mb-10"
            >
              <h2 className="text-3xl md:text-4xl font-semibold tracking-tight mb-3 font-['Plus_Jakarta_Sans']">
                Why Choose ToolsForge?
              </h2>
              <p className="text-muted-foreground">
                Built for professionals who demand the best
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {features.map((feature, index) => (
                <motion.div
                  key={feature.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="p-6 rounded-xl border border-border bg-card hover:border-primary/30 transition-colors"
                >
                  <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                    <feature.icon className="w-6 h-6 text-primary" />
                  </div>
                  <h3 className="font-semibold text-lg mb-2 font-['Plus_Jakarta_Sans']">{feature.title}</h3>
                  <p className="text-sm text-muted-foreground">{feature.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* SEO Content Section */}
        <section className="py-16 px-6 bg-muted/20">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <h2 className="text-2xl font-semibold mb-6 font-['Plus_Jakarta_Sans']">
                Free Online Tools for Everyone
              </h2>
              <div className="prose prose-invert max-w-none text-muted-foreground">
                <p>
                  ToolsForge is your one-stop destination for free online tools. Whether you're a developer needing to format JSON, 
                  a designer working with colors, or a content creator optimizing for SEO, we have the tools you need. 
                  All 180+ tools are completely free, require no signup, and process everything locally in your browser for maximum privacy.
                </p>
                <p>
                  Our most popular tools include the <Link to="/tools/qr-code-generator" className="text-primary hover:underline">QR Code Generator</Link> for 
                  creating scannable codes, the <Link to="/tools/json-formatter" className="text-primary hover:underline">JSON Formatter</Link> for 
                  beautifying API responses, and the <Link to="/tools/password-generator" className="text-primary hover:underline">Password Generator</Link> for 
                  creating secure passwords. Each tool is designed with simplicity and efficiency in mind.
                </p>
                <p>
                  Explore our <Link to="/category/developer-tools" className="text-primary hover:underline">Developer Tools</Link> for coding utilities, 
                  <Link to="/category/seo-tools" className="text-primary hover:underline"> SEO Tools</Link> for search optimization, 
                  <Link to="/category/text-tools" className="text-primary hover:underline"> Text Tools</Link> for content editing, and 
                  <Link to="/category/converters" className="text-primary hover:underline"> Converters</Link> for format transformations.
                </p>
              </div>
            </motion.div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 px-6" data-testid="cta-section">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="p-10 rounded-2xl border border-border bg-gradient-to-b from-primary/5 to-accent/5"
            >
              <h2 className="text-3xl md:text-4xl font-semibold tracking-tight mb-4 font-['Plus_Jakarta_Sans']">
                Ready to Get Started?
              </h2>
              <p className="text-lg text-muted-foreground mb-8">
                Start using our free tools right now. No signup required.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button 
                  size="lg" 
                  className="glow-blue text-base px-8"
                  asChild
                >
                  <Link to="/all-tools">
                    Browse All Tools
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Link>
                </Button>
                <Button 
                  size="lg" 
                  variant="outline"
                  className="text-base px-8"
                  onClick={login}
                  data-testid="cta-login"
                >
                  Sign In to Save Tools
                </Button>
              </div>
            </motion.div>
          </div>
        </section>
      </div>
    </>
  );
};

export default LandingPage;
